import { useState } from "react";
import { useAdmin } from "../admin";
import { weekStart } from "../lib/dates";
import { COACHING_LINE, addWeeks, exportCsv, exportFileName, mondayOf } from "../lib/export";
import type { ScoreExport } from "../lib/export";
import { errorText, supabase } from "../supabase";

// Milestone 8: the practice export, for planning coaching in a spreadsheet. One row
// per person per week with what they were given, what they finished and their skill
// levels. Levels only, never a score or rank, and the file says it is for coaching.

export default function Export() {
  const { org, sites } = useAdmin();
  const thisWeek = weekStart();
  const [site, setSite] = useState("");
  const [from, setFrom] = useState(addWeeks(thisWeek, -3));
  const [to, setTo] = useState(thisWeek);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ kind: "ok" | "error"; text: string } | null>(null);

  async function download() {
    setBusy(true);
    setNotice(null);
    const { data, error } = await supabase.rpc("admin_score_export", {
      p_from: from,
      p_to: to,
      p_site: site || null,
    });
    setBusy(false);
    if (error) {
      setNotice({ kind: "error", text: errorText(error) });
      return;
    }
    const result = data as ScoreExport;
    if (result.rows.length === 0) {
      setNotice({ kind: "error", text: "Nobody was given or did any practice in these weeks, so there is nothing to export." });
      return;
    }
    const siteName = sites.find((s) => s.id === site)?.name ?? null;
    const csv = exportCsv(result, org.name, siteName, new Date().toISOString().slice(0, 10));
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = exportFileName(org.name, result.from, result.to);
    a.click();
    URL.revokeObjectURL(url);
    setNotice({ kind: "ok", text: `Downloaded ${result.rows.length} rows, for the weeks from ${result.from} to ${result.to}.` });
  }

  return (
    <>
      <div className="page-head">
        <h1>Practice export</h1>
      </div>

      <div className="notice warn" style={{ marginBottom: 20 }}>
        <strong>For coaching only.</strong> {COACHING_LINE.replace(/^Coaching only\. /, "")} The file carries this line
        at the top too.
      </div>

      <div className="grid-2">
        <div className="card stack">
          <h2>Download a CSV</h2>
          <label className="field">
            Site
            <select value={site} onChange={(e) => setSite(e.target.value)}>
              <option value="">All sites</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <div className="row">
            <label className="field">
              From the week of
              <input type="date" value={from} max={thisWeek} onChange={(e) => e.target.value && setFrom(mondayOf(e.target.value))} />
            </label>
            <label className="field">
              To the week of
              <input type="date" value={to} max={thisWeek} onChange={(e) => e.target.value && setTo(mondayOf(e.target.value))} />
            </label>
          </div>
          <p className="small muted">Weeks start on Monday. Up to 26 weeks at a time.</p>
          <div>
            <button className="btn" disabled={busy} onClick={download}>
              {busy ? "Preparing…" : "Download CSV"}
            </button>
          </div>
          {notice && <div className={`notice ${notice.kind}`}>{notice.text}</div>}
        </div>

        <div className="card">
          <h2>What is in it</h2>
          <p className="muted">
            One row for each person and week in which they were given a scenario or practised, in order of week, site
            and name. Nobody is ranked.
          </p>
          <ul className="small">
            <li>
              <strong>Scenarios given</strong> and <strong>done</strong>: what they had that week and finished within
              it.
            </li>
            <li>
              <strong>Strong, improving, starting</strong>: their skills at each level by the end of that week, the same
              levels they see on their own phone. Strong means at least three practices with the best answer at the
              first choice four times in five; improving means the best answer at least once.
            </li>
            <li>People who have since left are kept for the weeks they worked.</li>
          </ul>
          <p className="small muted">Opens in Excel or Google Sheets. Names in Marathi and Hindi show correctly.</p>
        </div>
      </div>

      <p className="footer-note">Scores guide coaching. They are never used for discipline or pay.</p>
    </>
  );
}
